import React, { useState, useEffect } from 'react';
import { timesheetAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Clock, Plus, Check, Send } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_COLORS = { draft: '#5B616E', submitted: '#9A6B1A', approved: '#1F7A5A' };

export default function AdminTimesheets() {
  const [timesheets, setTimesheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentWeek, setCurrentWeek] = useState('');
  const [entries, setEntries] = useState([{ day: 'Mon', hours: 0, project: '', notes: '' }]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const now = new Date();
    const startOfYear = new Date(now.getFullYear(), 0, 1);
    const week = Math.ceil(((now - startOfYear) / 86400000 + startOfYear.getDay() + 1) / 7);
    setCurrentWeek(`${now.getFullYear()}-W${String(week).padStart(2, '0')}`);
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await timesheetAPI.list();
      setTimesheets(r.data.timesheets || []);
    } catch { toast.error('Failed to load timesheets'); }
    finally { setLoading(false); }
  };

  const totalHours = entries.reduce((s, e) => s + parseFloat(e.hours || 0), 0);

  const handleSave = async () => {
    setSaving(true);
    try {
      await timesheetAPI.log({ week: currentWeek, entries, total_hours: totalHours });
      toast.success('Timesheet saved');
      load();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleSubmit = async (id) => {
    try { await timesheetAPI.submit(id); toast.success('Submitted for approval'); load(); }
    catch { toast.error('Failed'); }
  };

  const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><Clock className="w-6 h-6 text-[#B89B5E]" /> Timesheets</h1>
        <p className="text-sm text-[#5B616E] mt-1">Log and track working hours by week</p>
      </div>

      <Card className="bg-white border-[#E7E0D6]">
        <CardHeader><CardTitle className="text-sm">Current Week — {currentWeek}</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            {DAYS.map(day => {
              const entry = entries.find(e => e.day === day) || { day, hours: 0, project: '', notes: '' };
              const idx = entries.findIndex(e => e.day === day);
              const update = (field, val) => {
                const next = [...entries];
                if (idx === -1) next.push({ ...entry, [field]: val });
                else next[idx] = { ...entry, [field]: val };
                setEntries(next.filter(e => DAYS.includes(e.day)));
              };
              return (
                <div key={day} className="grid grid-cols-4 gap-2 items-center">
                  <span className="text-sm font-medium w-12">{day}</span>
                  <Input type="number" min={0} max={24} step={0.5} value={entry.hours || ''} onChange={e => update('hours', parseFloat(e.target.value) || 0)} placeholder="Hrs" className="text-xs" />
                  <Input value={entry.project || ''} onChange={e => update('project', e.target.value)} placeholder="Project" className="text-xs" />
                  <Input value={entry.notes || ''} onChange={e => update('notes', e.target.value)} placeholder="Notes" className="text-xs" />
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-between pt-2 border-t border-[#E7E0D6]">
            <span className="text-sm font-medium">Total: <strong>{totalHours.toFixed(1)}h</strong></span>
            <Button onClick={handleSave} disabled={saving} className="bg-[#121316] text-white hover:bg-[#1B1D22] text-xs">
              {saving ? 'Saving…' : 'Save Timesheet'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border-[#E7E0D6]">
        <CardHeader><CardTitle className="text-sm">My Timesheets</CardTitle></CardHeader>
        <CardContent>
          {loading ? <Skeleton className="h-32" /> : timesheets.length === 0 ? (
            <p className="text-sm text-[#5B616E] text-center py-4">No timesheets yet.</p>
          ) : (
            <div className="space-y-2">
              {timesheets.map(ts => (
                <div key={ts.id} className="flex items-center justify-between p-3 border border-[#E7E0D6] rounded-lg">
                  <div>
                    <p className="text-sm font-medium">{ts.week}</p>
                    <p className="text-xs text-[#5B616E]">{ts.total_hours}h logged</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge style={{ background: `${STATUS_COLORS[ts.status] || '#5B616E'}20`, color: STATUS_COLORS[ts.status] || '#5B616E' }} className="text-[10px]">{ts.status}</Badge>
                    {ts.status === 'draft' && (
                      <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => handleSubmit(ts.id)}>
                        <Send className="w-3 h-3 mr-1" /> Submit
                      </Button>
                    )}
                    {ts.status === 'approved' && <Check className="w-4 h-4 text-[#1F7A5A]" />}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
